import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CalendarPlus, Send, BookOpen, Copy, Sparkles } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { useFamily } from "../context/FamilyContext";
import { api, errMsg, fmtDate, fmtTime, money, firstName } from "../lib/api";
import { ReportCard } from "../components/ReportCard";
import { Celebrate } from "../components/Celebrate";

function BreakMeter({ days }) {
  const tone = days == null || days < 8 ? "bg-white border-hf-line" : days < 15 ? "bg-hf-coralsoft border-hf-coral/25" : "bg-hf-coralsoft border-hf-coral/40";
  const msg = days == null ? "No breaks yet. Let's plan your first one." : days < 8 ? "Nice. Keep it going." : days < 15 ? "A break would help soon." : "It's been a while. You deserve a break.";
  return (
    <div data-testid="break-meter" className={`rounded-xl border-[1.5px] p-6 ${tone}`}>
      <p className="hf-eyebrow">Break Meter</p>
      <div className="mt-2 flex items-end gap-3">
        <span data-testid="break-meter-days" className="font-heading text-6xl font-semibold leading-none text-hf-green">{days ?? 0}</span>
        <span className="pb-1 text-lg text-hf-ink">{days === 1 ? "day" : "days"} since your last break</span>
      </div>
      <div className="mt-4 flex gap-1.5" aria-hidden="true">
        {Array.from({ length: 14 }, (_, i) => (
          <span key={i} className={`h-2 flex-1 rounded-full ${i < Math.min(days ?? 0, 14) ? "bg-hf-coral" : "bg-hf-creamdeep"}`} />
        ))}
      </div>
      <p className="mt-3 text-base text-hf-ink/80">{msg}</p>
    </div>
  );
}

function Balance({ b, program, isDemo, upcoming }) {
  const pct = b.allowance ? Math.min(100, (b.used / b.allowance) * 100) : 0;
  return (
    <div data-testid="respite-balance-card" className="rounded-xl bg-hf-greendark p-6 text-hf-onprimary">
      <div className="flex items-start justify-between">
        <p className="text-[13px] font-semibold uppercase tracking-[0.12em] text-hf-sun">Respite Balance</p>
        {isDemo && <span className="rounded-full border border-hf-onprimary/25 px-3 py-0.5 text-xs">Fictional program</span>}
      </div>
      <p className="mt-1 text-[15px] text-hf-onprimary/80">{program?.name || "Your funding"} · this year</p>
      {b.allowance ? (
        <>
          <p data-testid="respite-balance-remaining" className="mt-5 font-heading text-5xl">{money(b.remaining)}</p>
          <p className="text-base text-hf-onprimary/85">left of {money(b.allowance)}. About {b.remaining_hours} hours of breaks.</p>
          <div className="mt-5 h-2 overflow-hidden rounded-full bg-hf-onprimary/15">
            <div className="h-full rounded-full bg-hf-sun" style={{ width: `${pct}%` }} />
          </div>
          <p data-testid="respite-balance-used" className="mt-2 text-[13px] text-hf-onprimary/75">{money(b.used)} used · {b.used_hours} hours</p>
        </>
      ) : (
        <p className="mt-5 text-base">We'll help you confirm your allowance. {b.used_hours ? `${b.used_hours} hours used so far.` : ""}</p>
      )}
      {upcoming.length > 0 && (
        <div className="mt-6 border-t border-dashed border-hf-onprimary/30 pt-4">
          <p className="text-[13px] font-semibold text-hf-onprimary/75">Coming up</p>
          {upcoming.slice(0, 3).map((v) => (
            <p key={v.id} data-testid={`upcoming-visit-${v.id}`} className="mt-1 text-base">
              {fmtDate(v.scheduled_date)} · {fmtTime(v.start_time)} · {v.hours} hrs
            </p>
          ))}
        </div>
      )}
    </div>
  );
}

function RestChart({ scores }) {
  if (!scores?.length) return null;
  return (
    <div data-testid="rest-chart" className="hf-card">
      <p className="hf-eyebrow">How rested you feel</p>
      <div className="mt-5 flex h-36 items-end gap-3">
        {scores.slice(-8).map((s, i) => (
          <div key={i} className="flex flex-1 flex-col items-center gap-2">
            <span className="text-sm font-semibold text-hf-green">{s.score}</span>
            <div className="w-full rounded-t-xl bg-hf-green/80" style={{ height: `${(s.score / 5) * 100}px` }} />
            <span className="text-xs text-hf-muted">{s.label ? "Start" : fmtDate(s.at).split(", ")[1] || ""}</span>
          </div>
        ))}
      </div>
      <p className="mt-3 text-base text-hf-muted">1 is running on empty. 5 is well rested.</p>
    </div>
  );
}

function BookDialog({ open, setOpen, onBooked }) {
  const { fid } = useFamily();
  const tomorrow = new Date(Date.now() + 864e5).toISOString().slice(0, 10);
  const [f, setF] = useState({ date: tomorrow, start_time: "13:00", hours: 3 });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/families/${fid}/visits`, { ...f, hours: Number(f.hours) });
      setOpen(false);
      onBooked(r.data);
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent data-testid="book-break-dialog" className="max-w-md rounded-2xl bg-hf-cream">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Book a break</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <label className="block text-base font-semibold">Day</label>
          <input data-testid="book-date-input" type="date" className="hf-input" value={f.date} min={tomorrow} onChange={(e) => setF({ ...f, date: e.target.value })} />
          <label className="block text-base font-semibold">Start time</label>
          <input data-testid="book-time-input" type="time" className="hf-input" value={f.start_time} onChange={(e) => setF({ ...f, start_time: e.target.value })} />
          <label className="block text-base font-semibold">How many hours?</label>
          <select data-testid="book-hours-select" className="hf-input" value={f.hours} onChange={(e) => setF({ ...f, hours: e.target.value })}>
            {[1, 2, 3, 4, 5, 6].map((h) => <option key={h} value={h}>{h} hours</option>)}
          </select>
          <button data-testid="book-submit-button" disabled={busy} onClick={submit} className="hf-btn hf-btn-coral w-full">Book my break</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Helpers({ helpers }) {
  if (!helpers.length) return null;
  const copy = (h) => {
    navigator.clipboard?.writeText(`${window.location.origin}/join/${h.invite_token}`);
    toast.success("Invite link copied");
  };
  const label = { ready: "Ready for visits", onboarding: "Getting set up", ask_sent: "Ask sent" };
  return (
    <div data-testid="helpers-list" className="hf-card">
      <p className="hf-eyebrow">Your people</p>
      <div className="mt-3 divide-y divide-hf-line">
        {helpers.map((h) => (
          <div key={h.id} data-testid={`helper-row-${h.id}`} className="flex items-center justify-between gap-3 py-3">
            <div>
              <p className="text-lg font-semibold">{h.name}</p>
              <p className="text-base text-hf-muted">{h.relationship} · {label[h.status]}</p>
            </div>
            {h.status !== "ready" && h.invite_token && (
              <button data-testid={`helper-copy-link-${h.id}`} onClick={() => copy(h)} className="flex h-12 w-12 items-center justify-center rounded-full bg-hf-sage text-hf-green" aria-label="Copy invite link">
                <Copy className="h-5 w-5" />
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function SetupCard() {
  const { fid, refresh, data } = useFamily();
  const [name, setName] = useState("");
  if (data.family.recipient?.name) return null;
  const save = async () => {
    await api.put(`/families/${fid}`, { recipient_name: name });
    refresh();
  };
  return (
    <div data-testid="setup-card" className="hf-card">
      <h2 className="font-heading text-xl font-semibold">Who do you care for?</h2>
      <p className="mt-1 text-base text-hf-muted">A first name or nickname is plenty. Like "Mom" or "Lolo Ben".</p>
      <input data-testid="setup-recipient-input" className="hf-input mt-4" value={name} onChange={(e) => setName(e.target.value)} placeholder="Their name" />
      <button data-testid="setup-recipient-save" disabled={!name.trim()} onClick={save} className="hf-btn hf-btn-primary mt-3 w-full">Save</button>
    </div>
  );
}

function SavedResults({ elig }) {
  if (!elig?.results?.length) return null;
  return (
    <div data-testid="saved-eligibility" className="hf-card">
      <p className="hf-eyebrow">You may be eligible for</p>
      <ul className="mt-3 space-y-2">
        {elig.results.map((r) => <li key={r.key} className="text-lg">{r.title}</li>)}
      </ul>
      <p className="mt-3 text-base text-hf-muted">We'll help you confirm each one.</p>
    </div>
  );
}

export default function Dashboard() {
  const { data, base, refresh, isDemo } = useFamily();
  const [booking, setBooking] = useState(false);
  const [party, setParty] = useState(false);
  const fam = data.family;
  const upcoming = data.visits.filter((v) => v.status === "upcoming");
  const last = data.reports[0];
  const lastVisit = last && data.visits.find((v) => v.id === last.visit_id);
  const onBooked = (r) => {
    refresh();
    if (r.celebrate) setParty(true);
    else toast.success("Break booked. Go rest.");
  };
  const actions = [
    ["Book a break", CalendarPlus, () => setBooking(true), "quick-book-break"],
    ["Send a brief", Send, `${base}/brief`, "quick-send-brief"],
    ["Read last report", BookOpen, last ? `${base}/reports/${last.id}` : null, "quick-last-report"],
  ];
  return (
    <div className="space-y-6">
      <div className="hf-rise">
        <p className="hf-eyebrow">Taking a break is caregiving, too.</p>
        <h1 data-testid="dashboard-greeting" className="mt-2 font-heading text-4xl font-semibold">Hi {firstName(fam.caregiver_name) || "there"}.</h1>
        {fam.recipient?.name && <p className="mt-2 text-lg text-hf-muted">Caring for {fam.recipient.name}{fam.recipient.age ? `, ${fam.recipient.age}` : ""}.</p>}
      </div>
      <SetupCard />
      <div className="grid grid-cols-3 gap-3">
        {actions.map(([label, Icon, to, tid]) => {
          const cls = "flex min-h-[104px] flex-col items-center justify-center gap-2 rounded-xl border-[1.5px] border-hf-line bg-white p-3 text-center text-[15px] font-semibold text-hf-green transition-transform active:scale-95";
          if (typeof to === "function") return <button key={tid} data-testid={tid} onClick={to} className={cls}><Icon className="h-7 w-7 text-hf-coral" />{label}</button>;
          if (!to) return <span key={tid} data-testid={tid} className={`${cls} opacity-50`}><Icon className="h-7 w-7" />{label}</span>;
          return <Link key={tid} data-testid={tid} to={to} className={cls}><Icon className="h-7 w-7 text-hf-coral" />{label}</Link>;
        })}
      </div>
      <BreakMeter days={data.days_since_break} />
      <Balance b={data.balance} program={fam.program} isDemo={isDemo} upcoming={upcoming} />
      {last && (
        <Link to={`${base}/reports/${last.id}`} data-testid="dashboard-last-report" className="block transition-transform active:scale-[0.99]">
          <ReportCard report={last} visit={lastVisit} compact />
        </Link>
      )}
      {data.reports.some((r) => r.needs_agreement_update) && (
        <Link to={`${base}/agreement`} data-testid="dashboard-agreement-nudge" className="flex items-center gap-3 rounded-xl border-[1.5px] border-hf-coral/25 bg-hf-coralsoft p-5 text-base">
          <Sparkles className="h-6 w-6 shrink-0 text-hf-coraldark" /> Something to adjust? Update your agreement together.
        </Link>
      )}
      <RestChart scores={fam.rest_scores} />
      <SavedResults elig={fam.eligibility} />
      <Helpers helpers={data.helpers} />
      <BookDialog open={booking} setOpen={setBooking} onBooked={onBooked} />
      <Celebrate open={party} onClose={() => setParty(false)} />
    </div>
  );
}
